import * as React from "react";
import { motion, useReducedMotion } from "motion/react";
import { Bell, MessageCircleHeart, PanelLeftClose, PanelLeftOpen } from "lucide-react";
import { cn } from "@eduviaos/ui-kit";
import type { NavItem } from "./BottomNav";
import { InstitutionMark } from "./InstitutionMark";
import { verticalLabel, type Institution } from "../lib/institution";
import { usePortalLocale } from "../lib/i18n/usePortalLocale";

const SIDENAV_COLLAPSED_KEY = "portal-sidenav-collapsed";

/**
 * Desktop primary navigation (≥lg). A sticky, full-height rail that replaces
 * the mobile floating BottomNav: brand → vertical nav → prominent "Ask Ziggy"
 * → notifications. Hidden below lg where BottomNav takes over.
 */
export function SideNav({
  institution,
  items,
  activeId,
  onSelect,
  onAskZiggy,
  onNotifications,
  /**
   * Real unread count. `undefined` means "we don't know" (the prototype
   * verticals read mock notices), which still paints a plain dot; a known `0`
   * paints nothing rather than claiming there is something to read.
   */
  unreadNotifications,
  learnerLocale = true,
}: {
  institution: Institution;
  items: NavItem[];
  activeId: string;
  onSelect: (id: string) => void;
  onAskZiggy?: () => void;
  onNotifications?: () => void;
  unreadNotifications?: number;
  /** Read the learner's saved language. Off for the Team persona (no learner prefs). */
  learnerLocale?: boolean;
}) {
  const reduce = useReducedMotion();
  const { strings: s } = usePortalLocale(learnerLocale);
  const [collapsed, setCollapsed] = React.useState(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem(SIDENAV_COLLAPSED_KEY) === "1";
  });

  function toggleCollapsed() {
    setCollapsed((prev) => {
      const next = !prev;
      window.localStorage.setItem(SIDENAV_COLLAPSED_KEY, next ? "1" : "0");
      return next;
    });
  }

  return (
    <aside
      className={cn(
        "border-border/50 bg-card/60 sticky top-0 hidden h-dvh shrink-0 flex-col border-r backdrop-blur-xl transition-[width] duration-200 ease-out lg:flex",
        collapsed ? "w-[4.25rem] px-2 py-5" : "w-64 px-4 py-6 xl:w-72",
      )}
    >
      <div className={cn("flex items-center gap-2.5", collapsed ? "justify-center px-0" : "px-2")}>
        <InstitutionMark vertical={institution.vertical} name={institution.name} size="sm" />
        {!collapsed && (
          <div className="min-w-0 flex-1 leading-tight">
            <p className="truncate text-sm font-semibold">{institution.shortName}</p>
            <p className="text-muted-foreground truncate text-[0.7rem]">
              {verticalLabel(institution.vertical)}
            </p>
          </div>
        )}
        {!collapsed && (
          <button
            type="button"
            aria-label={s.collapseSidebar}
            onClick={toggleCollapsed}
            className="text-muted-foreground hover:text-foreground grid size-8 shrink-0 place-items-center rounded-lg transition-colors"
          >
            <PanelLeftClose className="size-4" />
          </button>
        )}
      </div>

      {collapsed && (
        <button
          type="button"
          aria-label={s.expandSidebar}
          onClick={toggleCollapsed}
          className="text-muted-foreground hover:text-foreground mx-auto mt-3 grid size-9 place-items-center rounded-lg transition-colors"
        >
          <PanelLeftOpen className="size-4" />
        </button>
      )}

      <nav
        aria-label={s.primaryNavAria}
        className={cn("mt-8 flex flex-1 flex-col gap-1", collapsed && "mt-4 items-center")}
      >
        {items.map((item) => {
          const active = item.id === activeId;
          const Icon = item.icon;
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => onSelect(item.id)}
              aria-current={active ? "page" : undefined}
              aria-label={collapsed ? item.label : undefined}
              title={collapsed ? item.label : undefined}
              className={cn(
                "relative flex items-center rounded-xl text-sm font-medium transition-colors duration-150",
                collapsed ? "size-10 justify-center" : "gap-3 px-3 py-2.5",
                active ? "text-primary" : "text-muted-foreground hover:text-foreground",
              )}
            >
              {active && (
                <motion.span
                  layoutId={reduce ? undefined : "sidenav-active"}
                  transition={{ type: "spring", stiffness: 480, damping: 36 }}
                  className="bg-primary/12 absolute inset-0 rounded-xl"
                />
              )}
              <Icon className={cn("relative size-5 shrink-0", active && "fill-primary/15")} />
              {!collapsed && <span className="relative truncate">{item.label}</span>}
            </button>
          );
        })}
      </nav>

      {(onAskZiggy || onNotifications) && (
        <div className={cn("mt-4 space-y-2", collapsed && "flex flex-col items-center")}>
          {onAskZiggy && (
            <button
              type="button"
              onClick={onAskZiggy}
              aria-label={s.askZiggy}
              title={collapsed ? s.askZiggy : undefined}
              className={cn(
                "from-primary bg-primary flex items-center justify-center rounded-xl bg-gradient-to-br to-[color-mix(in_oklch,var(--color-primary)_60%,oklch(0.66_0.2_340))] font-semibold text-white shadow-[var(--shadow-float)] transition-transform duration-150 ease-out hover:-translate-y-0.5 active:scale-[0.98]",
                collapsed ? "size-10" : "w-full gap-2 px-3 py-2.5 text-sm",
              )}
            >
              <MessageCircleHeart className="size-5 shrink-0" />
              {!collapsed && s.askZiggy}
            </button>
          )}
          {onNotifications && (
            <button
              type="button"
              onClick={onNotifications}
              aria-label={
                unreadNotifications
                  ? s.notificationsWithUnread(unreadNotifications)
                  : s.notifications
              }
              title={collapsed ? s.notifications : undefined}
              className={cn(
                "text-muted-foreground hover:text-foreground hover:bg-muted/60 relative flex items-center rounded-xl font-medium transition-colors",
                collapsed ? "size-10 justify-center" : "w-full gap-3 px-3 py-2.5 text-sm",
              )}
            >
              <span className="relative">
                <Bell className="size-5" />
                {(unreadNotifications === undefined || unreadNotifications > 0) && (
                  <span className="bg-celebrate ring-card absolute -right-0.5 -top-0.5 size-2 rounded-full ring-2" />
                )}
              </span>
              {!collapsed && s.notifications}
              {!collapsed && Boolean(unreadNotifications) && (
                <span className="bg-celebrate/15 text-celebrate ml-auto rounded-full px-2 py-0.5 text-xs font-semibold tabular-nums">
                  {unreadNotifications}
                </span>
              )}
            </button>
          )}
        </div>
      )}
    </aside>
  );
}
